// ── Stale-chunk self-heal ──
//
// A lazy route's JS/CSS chunk can fail to load for two reasons:
//   1. The cached index.html references a previous build's hashes, and the
//      deploy has already deleted those files (CRA empties build/ each time).
//   2. The fetch was aborted mid-flight by a navigation — the case that hit
//      fresh browser sessions on a brand-new tenant during login.
//
// Either way one reload fixes it, so reload once and let the fresh document
// pull the current hashes. A repeat failure inside the cooldown is a real
// outage, so it falls through to RouteErrorBoundary rather than reload-looping.
//
// Both the window-level listeners (src/index.js) and lazyWithRetry
// (src/routes/index.jsx) share this module so they cannot disagree about
// whether a reload has already been attempted.

const RELOAD_KEY = 'xenbox:chunk-reload-at';
const RELOAD_COOLDOWN_MS = 60_000;

const CHUNK_MESSAGE_RE =
  /Loading (chunk|CSS chunk) [\w-]+ failed|Failed to fetch dynamically imported module|error loading dynamically imported module/i;

/** True for a thrown Error / rejection value that represents a chunk failure. */
export function isChunkLoadError(err) {
  if (!err) return false;
  if (err.name === 'ChunkLoadError') return true;
  return CHUNK_MESSAGE_RE.test(String(err.message || err));
}

/**
 * True for a subresource load failure on a webpack-emitted chunk.
 *
 * These arrive as a plain Event whose target is the <link>/<script> element —
 * there is no `.error` and no message to pattern-match, so the element's URL is
 * the only signal. Such events also do not bubble, which is why the listener in
 * src/index.js must be registered in the capture phase.
 */
export function isChunkResourceEvent(event) {
  const el = event?.target;
  if (!el || el === window || !el.tagName) return false;
  if (el.tagName === 'LINK') return /\/static\/css\/.+\.chunk\.css/i.test(el.href || '');
  if (el.tagName === 'SCRIPT') return /\/static\/js\/.+\.chunk\.js/i.test(el.src || '');
  return false;
}

/**
 * Reload once per cooldown window. Returns false if a reload was already
 * attempted recently, so callers can rethrow and surface the error instead.
 */
export function attemptChunkReload() {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
    if (Date.now() - last < RELOAD_COOLDOWN_MS) return false;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch (_) {
    // sessionStorage unavailable (private mode) — reload anyway rather than
    // leaving the user stuck on a broken shell.
  }
  window.location.reload();
  return true;
}
